/**
 * Every binding, as rows of key caps: press a row, press a key, and conflicts are settled loudly.
 *
 * One control over the whole map rather than a control per action, because the interesting moment
 * involves two rows at once: a captured key another action holds offers to move, and only the
 * thing that owns both rows can take it from one and give it to the other, repaint both, and
 * commit a map that still holds no code twice. The map logic itself is
 * [bindingOps](../../../core/input/bindings/bindingOps.ts) and is pure; this file is the DOM over
 * it, and every decision it commits went through those functions.
 *
 * Capture listens on the window in the capture phase and swallows the key, so pressing Space to
 * bind jump does not also press whatever button had focus. `Escape` always backs out, of the
 * capture and of a conflict, which is the same reason it can never be taken as a binding.
 *
 * Only `onCommit` fires, on the terms `choiceButtons.ts` states: a press is the decision. What is
 * committed is the serialised map, so one setting id persists every row.
 */

import { BINDABLE_ACTIONS } from '@/core/input/bindings/bindingContract';
import type { BindableActionId, BindingMap } from '@/core/input/bindings/bindingContract';
import { DEFAULT_BINDINGS } from '@/core/input/bindings/defaultBindings';
import {
  fromStored,
  rebind,
  resetBinding,
  swapBind,
  toStored,
} from '@/core/input/bindings/bindingOps';
import { codeLabel } from '@/ui/keyLabels';
import { el, setClass, setText, setVisible } from '@/ui/dom';
import { KEY_UNBOUND } from '@/text/keyText';
import {
  bindingConflictNote,
  SETTING_BINDING_ACTION_LABELS,
  SETTING_BINDING_CANCEL,
  SETTING_BINDING_PRESS,
  SETTING_BINDING_RESERVED,
  SETTING_BINDING_RESET,
  SETTING_BINDING_RESET_ALL,
  SETTING_BINDING_TAKE,
} from '@/text/settingsText';
import type {
  SettingControl,
  SettingControlDefinition,
  SettingControlHandle,
  SettingControlOptions,
  SettingValue,
} from '@/ui/settings/settingContract';

/** What a row is waiting on: nothing, a key, or the player's answer about a taken one. */
type Pending =
  | { readonly kind: 'idle' }
  | { readonly kind: 'capturing'; readonly action: BindableActionId }
  | {
      readonly kind: 'conflict';
      readonly action: BindableActionId;
      readonly code: string;
      readonly holder: BindableActionId;
    };

export const KEY_BINDING_ROWS: SettingControlDefinition = {
  kind: 'bindings',

  /** A stored map this build can read, or nothing. Out of range has no meaning for a map. */
  coerce(control: SettingControl, raw: unknown): SettingValue | null {
    if (control.kind !== 'bindings') return null;
    const map = fromStored(raw);
    return map === null ? null : toStored(map);
  },

  create(options: SettingControlOptions): SettingControlHandle {
    const control = options.definition.control;
    // The registry resolved this control by its own kind, so nothing else can arrive here. An
    // empty handle rather than a throw: a settings screen missing one row beats one that is blank.
    if (control.kind !== 'bindings') return { root: el('div', 'settings-control'), dispose: noop };

    let map: BindingMap = fromStored(options.value) ?? DEFAULT_BINDINGS;
    let pending: Pending = { kind: 'idle' };

    const root = el('div', 'settings-control settings-bindings');

    // One status line for the whole panel, spoken as well as drawn: a refusal or a conflict is
    // the answer to the player's last press, not decoration on a row.
    const note = el('p', 'settings-bindingNote');
    note.role = 'status';

    const take = el('button', 'settings-bindingAnswer', SETTING_BINDING_TAKE);
    take.type = 'button';
    const cancel = el('button', 'settings-bindingAnswer', SETTING_BINDING_CANCEL);
    cancel.type = 'button';
    const answers = el('div', 'settings-bindingAnswers');
    answers.append(take, cancel);

    const rows = new Map<BindableActionId, { keys: HTMLButtonElement }>();

    const commit = (next: BindingMap): void => {
      map = next;
      options.onCommit(toStored(map));
    };

    const paint = (): void => {
      for (const [action, row] of rows) {
        const capturing = pending.kind !== 'idle' && pending.action === action;
        const labels = map[action].map(codeLabel);
        setText(row.keys, capturing ? SETTING_BINDING_PRESS : joined(labels));
        setClass(row.keys, 'is-capturing', capturing);
      }
      setVisible(answers, pending.kind === 'conflict');
      if (pending.kind === 'conflict') {
        setText(note, bindingConflictNote(SETTING_BINDING_ACTION_LABELS[pending.holder]));
      }
    };

    const settle = (): void => {
      pending = { kind: 'idle' };
      paint();
    };

    const onCapturedKey = (event: KeyboardEvent): void => {
      if (pending.kind !== 'capturing') return;
      event.preventDefault();
      event.stopPropagation();

      if (event.code === 'Escape') {
        setText(note, '');
        settle();
        return;
      }

      const result = rebind(map, pending.action, event.code);
      if (result.kind === 'reserved') {
        setText(note, SETTING_BINDING_RESERVED);
        settle();
        return;
      }
      if (result.kind === 'conflict') {
        const { action } = pending;
        pending = { kind: 'conflict', action, code: event.code, holder: result.holder };
        paint();
        return;
      }
      setText(note, '');
      commit(result.map);
      settle();
    };
    window.addEventListener('keydown', onCapturedKey, true);

    const onTake = (): void => {
      if (pending.kind !== 'conflict') return;
      setText(note, '');
      commit(swapBind(map, pending.action, pending.code));
      settle();
    };
    const onCancel = (): void => {
      setText(note, '');
      settle();
    };
    take.addEventListener('click', onTake);
    cancel.addEventListener('click', onCancel);

    for (const action of BINDABLE_ACTIONS) {
      const row = el('div', 'settings-bindingRow');
      const label = el('span', 'settings-bindingAction', SETTING_BINDING_ACTION_LABELS[action]);

      const keys = el('button', 'settings-bindingKeys');
      keys.type = 'button';
      keys.ariaLabel = SETTING_BINDING_ACTION_LABELS[action];
      keys.addEventListener('click', () => {
        setText(note, '');
        pending = { kind: 'capturing', action };
        paint();
      });

      const reset = el('button', 'settings-bindingReset', SETTING_BINDING_RESET);
      reset.type = 'button';
      reset.addEventListener('click', () => {
        setText(note, '');
        commit(resetBinding(map, action));
        settle();
      });

      row.append(label, keys, reset);
      root.append(row);
      rows.set(action, { keys });
    }

    const resetAll = el('button', 'settings-bindingResetAll', SETTING_BINDING_RESET_ALL);
    resetAll.type = 'button';
    const onResetAll = (): void => {
      setText(note, '');
      commit(DEFAULT_BINDINGS);
      settle();
    };
    resetAll.addEventListener('click', onResetAll);

    root.append(answers, note, resetAll);
    paint();

    return {
      root,
      dispose(): void {
        window.removeEventListener('keydown', onCapturedKey, true);
        // Row and answer listeners die with their elements; the window's is the one that would
        // outlive the screen.
        root.remove();
      },
    };
  },
};

/** The synonyms on one row, read as alternatives. An empty row is a visible absence. */
function joined(labels: readonly string[]): string {
  return labels.length === 0 ? KEY_UNBOUND : labels.join(' / ');
}

/** What the unreachable branch in `create` hands back, so a missing row disposes like any other. */
function noop(): void {
  return undefined;
}
